import { useState } from 'react';
import { Button, Group, Switch, Text } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { InsuranceModal } from './InsuranceModal';
import { wizardInsuranceFooterMessages } from './wizard-insurance-footer-messages';
import styles from './WizardInsuranceFooter.module.css';

import type { WizardInsurance } from '../../freight-booking-wizard-types';

type WizardInsuranceFooterProps = {
  insurance: WizardInsurance;
  onInsuranceChange: (next: WizardInsurance) => void;
  zIndex?: number;
};

export const WizardInsuranceFooter = ({ insurance, onInsuranceChange, zIndex }: WizardInsuranceFooterProps) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Switch shows on immediately for a snappy toggle, but isInsured isn't committed to the parent
  // (and therefore can't trigger a re-quote) until the modal is saved — turning off is still immediate.
  const [isPendingToggleOn, setIsPendingToggleOn] = useState(false);

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setIsPendingToggleOn(true);
      setIsModalOpen(true);
    } else {
      setIsPendingToggleOn(false);
      onInsuranceChange({ ...insurance, isInsured: false });
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsPendingToggleOn(false);
  };

  const handleModalSave = (next: WizardInsurance) => {
    onInsuranceChange({ ...next, isInsured: true });
  };

  const switchRow = (
    <Group justify='space-between' wrap='nowrap'>
      <Switch
        checked={insurance.isInsured || isPendingToggleOn}
        classNames={{ label: styles.switchLabel }}
        data-testid='insurance-toggle'
        label={intl.formatMessage(wizardInsuranceFooterMessages.insureThisShipment)}
        onChange={(e) => handleToggle(e.currentTarget.checked)}
      />
      {insurance.isInsured && (
        <Group className={styles.insuredValueRow} gap={12} wrap='nowrap'>
          <Group gap={2} wrap='nowrap'>
            <Text c='var(--adl-text-secondary)' size='sm'>
              {intl.formatMessage(wizardInsuranceFooterMessages.summaryInsuredValueLabel)}
            </Text>
            <Text fw={600} size='sm'>
              {insurance.insuredValue ? intl.formatNumber(insurance.insuredValue, { style: 'currency', currency: 'USD' }) : '—'}
            </Text>
          </Group>
          <Button data-testid='insurance-edit' onClick={() => setIsModalOpen(true)} variant='hyperlink'>
            {intl.formatMessage(wizardInsuranceFooterMessages.edit)}
          </Button>
        </Group>
      )}
    </Group>
  );

  return (
    <>
      <div
        className={clsx(styles.insuranceSection, insurance.isInsured && styles.insuranceSummaryBand)}
        data-testid='wizard-insurance-footer'
      >
        {switchRow}
      </div>
      <InsuranceModal insurance={insurance} onClose={handleModalClose} onSave={handleModalSave} opened={isModalOpen} zIndex={zIndex} />
    </>
  );
};
